<template>
  <CCard
    class="flex-shrink-1 pb-0 w-full flex flex-col justify-center"
    v-if="series?.length && mounted"
  >
    <div class="w-full">
      <h4
        class="text-xl text-dark font-semibold px-4 pt-4 pb-2 md:pt-5 md:pb-3 md:px-5"
      >
        {{ $t("dashboard.charts.course_title") }}
      </h4>
      <hr class="w-full h-[1px] bg-gray-300 mb-2" />
    </div>
    <div class="h-[280px] pb-3 my-4">
      <Doughnut :data="data" :options="options" class="h-full" />
    </div>
  </CCard>
</template>

<script setup lang="ts">
import { computed } from "vue";
import CCard from "@/components/Card/CardWrapper.vue";
import { useI18n } from "vue-i18n";
import { useMounted } from "@vueuse/core";
import { Chart as ChartJS, ArcElement, BarElement, Legend } from "chart.js";
import { Doughnut } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(ArcElement, BarElement, Legend, ChartDataLabels);

interface Props {
  win: number;
  lost: number;
}
const props = defineProps<Props>();
const { t } = useI18n();

const mounted = useMounted();

const series = computed(() => {
  return props?.win > 0 || props?.lost > 0
    ? [
        Number(
          ((props?.win / (props?.win + props?.lost)) * 100)?.toPrecision(1),
        ),
        Number(
          ((props?.lost / (props?.win + props?.lost)) * 100)?.toPrecision(1),
        ),
      ]
    : [100, 0];
});
const seriesPercentage = computed(() => {
  return props?.win > 0 || props?.lost > 0
    ? [
        Number(
          calculateWinLossPercentage(props?.win, props?.lost).winPercentage,
        ),
        Number(
          calculateWinLossPercentage(props?.win, props?.lost).lostPercentage,
        ),
      ]
    : [100, 0];
});
function calculateWinLossPercentage(win, lost) {
  const total = win + lost;

  if (total === 0) {
    return {
      winPercentage: 0,
      lostPercentage: 0,
    };
  }

  const winPercentage = (win / total) * 100;
  const lostPercentage = (lost / total) * 100;

  return {
    winPercentage: winPercentage.toFixed(0),
    lostPercentage: lostPercentage.toFixed(0),
  };
}
const data = computed(() => {
  return {
    labels:
      props?.win > 0 && props?.lost > 0
        ? [
            String(t("profit") + " (" + props?.win + ")"),
            String(t("loss") + " (" + props?.lost + ")"),
          ]
        : [
            String(t("profit") + " (" + 0 + ")"),
            String(t("loss") + " (" + 0 + ")"),
          ],
    items: series.value,
    datasets: [
      {
        backgroundColor: ["#5aef92", "#e85d5b"],
        data: series.value,
        borderWidth: 1,
      },
    ],
  };
});

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    datalabels: {
      color: "#fff", // Customize the label color
      formatter: (value, context) => {
        return seriesPercentage.value?.[context.dataIndex] + "%";
      },
      font: {
        weight: "medium",
        size: 14,
      },
      anchor: "end",
      align: "start",
      offset: 12,
    },
    legend: {
      labels: {
        boxWidth: 12,
        boxHeight: 12,
        padding: 16,
        font: {
          family: "InterDisplay",
          size: 14,
          weight: "bold",
        },
        color: "#121C25",
      },
      display: true,
      position: "bottom",
    },
  },
};

// const chartOptions = computed(() => {
//   return {
//     annotations: {},
//     chart: {
//       animations: {
//         enabled: true,
//         easing: "easeinout",
//         speed: 800,
//       },
//       fontFamily: "Inter",
//       id: "F5f9b",
//       height: 400,
//       toolbar: {
//         show: false,
//       },
//     },
//     plotOptions: {
//       pie: {
//         offsetY: 60,
//         customScale: 2.4,
//         // dataLabels: {
//         //   offset: 0,
//         // },
//       },
//     },
//     dataLabels: {
//       enabled: true,
//       style: {
//         fontSize: 4.5,
//         fontFamily: "InterDisplay",
//         fontWeight: 600,
//         colors: ["#ffffff", "#ffffff"],
//       },
//       dropShadow: {
//         enabled: false,
//       },
//     },
//     fill: {
//       opacity: 1,
//     },
//     grid: {
//       padding: {
//         right: 0,
//         bottom: 12,
//         left: 10,
//       },
//     },
//     labels: [t("profit"), t("loss")],
//     colors: ["#75ff9b", "#fb8180"],
//     stroke: {
//       show: false,
//       colors: ["#E34141", "#fff"],
//       dashArray: 2,
//       fill: {
//         type: "solid",
//         opacity: 0.85,
//         gradient: {
//           shade: "dark",
//           type: "horizontal",
//           shadeIntensity: 0.5,
//           inverseColors: true,
//           opacityFrom: 1,
//           opacityTo: 1,
//           stops: [0, 50, 100],
//           colorStops: [],
//         },
//       },
//     },
//     tooltip: {
//       enabled: false,
//       fillSeriesColor: true,
//       theme: "dark",
//     },
//     xaxis: {
//       labels: {
//         trim: true,
//         style: {},
//       },
//     },
//     yaxis: {
//       labels: {
//         style: {},
//       },
//       title: {
//         style: {
//           fontWeight: 700,
//         },
//       },
//     },
//   };
// });
// const options = computed(() => {
//   return {
//     ...chartOptions.value,
//     legend: {
//       show: true,
//       position: "bottom",
//       offsetY: 0,
//       fontSize: "14px",
//       fontWeight: 700,
//       fontFamily: "InterDisplay",
//       customLegendItems: [
//         String(t("profit") + " (" + props?.win + ")"),
//         String(t("loss") + " (" + props?.lost + ")"),
//       ],
//     },
//   };
// });
// onMounted(() => {
//   if (navigator?.vendor?.includes("Apple")) {
//     const chart = document.querySelector(".win-chart");
//     chart?.classList.add("apple");
//   }
// });
</script>

<style>
.win-chart.vue-apexcharts {
  height: 250px !important;
}
.win-chart .apexcharts-canvas {
  height: 100% !important;
}
.win-chart .apexcharts-svg {
  height: 100% !important;
}
.win-chart .apexcharts-svg > foreignObject {
  height: 100% !important;
}
.apple .apexcharts-legend {
  bottom: -36px !important;
}
</style>
